import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { API, fmtError, saveSession } from "@/lib/auth";

const inputCls =
  "w-full rounded-xl border border-white/12 bg-[#07090E] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-emerald-400/60";

const SifreSifirla = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(
    token ? "" : "Sıfırlama bağlantısı eksik. E-postandaki bağlantıyı kullan.",
  );
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Şifreler eşleşmiyor.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, {
        token,
        password,
      });
      saveSession(data);
      toast.success("Şifren güncellendi, hoş geldin.");
      navigate("/panel", { replace: true, state: { user: data } });
    } catch (err) {
      setError(fmtError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      data-testid="reset-page"
      className="flex min-h-screen flex-col bg-[#07090E] text-slate-50"
    >
      <header className="glass-panel border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" aria-label="Akış ana sayfa">
            <Logo size={30} />
          </Link>
          <Link
            to="/giris"
            data-testid="reset-back-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-emerald-400"
          >
            <ArrowLeft className="h-4 w-4" /> Girişe dön
          </Link>
        </div>
      </header>
      <div className="flex flex-1 items-center justify-center px-4 py-14">
        <motion.form
          data-testid="reset-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm rounded-3xl border border-white/8 bg-[#0D111A] p-8"
        >
          <span className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-400/25 bg-emerald-500/10">
            <KeyRound className="h-5 w-5 text-emerald-400" />
          </span>
          <h1 className="font-display text-center text-xl font-bold">
            Yeni şifre belirle
          </h1>
          <p className="mt-1.5 text-center text-sm text-slate-500">
            En az 8 karakter. Kaydettikten sonra otomatik giriş yapılır.
          </p>
          <div className="mt-7 space-y-3">
            <input
              data-testid="reset-password-input"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Yeni şifre"
              className={inputCls}
              disabled={!token}
            />
            <input
              data-testid="reset-confirm-input"
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Yeni şifre (tekrar)"
              className={inputCls}
              disabled={!token}
            />
            {error && (
              <div
                data-testid="reset-error"
                className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-300"
              >
                {error}
              </div>
            )}
            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={busy || !token}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Şifremi
              Güncelle
            </button>
          </div>
        </motion.form>
      </div>
    </main>
  );
};

export default SifreSifirla;
